import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ClerkProvider, SignIn, SignedIn, SignedOut, UserButton, useAuth } from '@clerk/clerk-react';

import { decideAccess } from '../core/access';
import { TEACHER_LIMITS, type QuotaLimits } from '../core/quota';
import type { Profile } from '../core/profile';
import { completeProfile, type StandardsDefault } from '../core/standards-default';
import {
  previewObjectivesAction,
  objectivesPageViewed,
  objectivesToolsListOpened,
  objectivesProfileSaved,
  objectivesReportViewed,
  objectivesUploadRejected,
  objectivesViewToggled,
  type AnalyticsEvent,
} from '../core/events';
import {
  UPLOAD_GATE,
  createAnalytics,
  nullAnalytics,
  stopAnalytics,
  type Analytics,
} from './analytics/client';
import { call, type GetToken, type ReportBody, type UsageSummary } from './api';
import AccessDenied from './components/AccessDenied';
import ProfileForm from './components/ProfileForm';
import Report from './components/Report';
import ObjectivesHeader from './components/ObjectivesHeader';
import ObjectivesWelcome, { focusOwnPlan } from './components/ObjectivesWelcome';
import PublicObjectives from './components/PublicObjectives';
import Uploader from './components/Uploader';
import { uploadAndRead as runUploadAndRead } from './run';

type Stage = 'loading' | 'denied' | 'profile' | 'upload' | 'report';

export interface Auth {
  getToken: () => Promise<string | null>;
  userId: string | null;
}

export interface AppProps {
  /** Supplied by whichever wrapper is in play — Clerk, or the browser-test identity. */
  auth: Auth;
  /**
   * Rendered in the header. Clerk's own components throw outside a `ClerkProvider`, so
   * `UserButton` is passed in by the wrapper that has one rather than imported here —
   * otherwise the browser-test path, which has no provider, dies on mount.
   */
  headerAction?: ReactNode;
  /** Injected in tests. In the browser the real adapter is built after sign-in. */
  analytics?: Analytics;
}

/**
 * The signed-in tool.
 *
 * Access is decided by the Worker, not here. The page asks `/api/me`, and a 403 renders
 * the denial with the reason it was given — it never re-derives the rule, because a rule
 * stated in two places is a rule that will disagree with itself.
 */
export function ObjectivesApp({ auth, headerAction, analytics: injected }: AppProps) {
  const { getToken, userId } = auth;
  const [analytics, setAnalytics] = useState<Analytics | null>(injected ?? null);

  const ownPlanRef = useRef<HTMLDivElement>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [stage, setStage] = useState<Stage>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [deniedReason, setDeniedReason] = useState<string | undefined>();
  const [profile, setProfile] = useState<Profile | null>(null);
  /** A partial shared default, prefilled into the form until a complete profile is saved. */
  const [draft, setDraft] = useState<StandardsDefault | null>(null);
  const [report, setReport] = useState<ReportBody | null>(null);
  const [busy, setBusy] = useState(false);
  const [profileError, setProfileError] = useState<{ field?: string; message: string } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /**
   * Every track call goes through here. The adapter already swallows vendor errors, but
   * this is the boundary that matters: a throw from an effect kills the mount, and a throw
   * from the submit handler loses the upload.
   */
  const track = useCallback((event: AnalyticsEvent) => {
    try {
      analytics?.track(event);
    } catch (error) {
      console.warn('[analytics] track failed', error);
    }
  }, [analytics]);

  /**
   * Clerk's `useAuth()` hands back a fresh `getToken` on every render. Closing over it
   * directly would give `api` a new identity each render, and the effect below — which
   * depends on `api` — would re-run forever. The ref keeps the callback stable and the
   * token current.
   */
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const api: GetToken = useCallback(() => tokenRef.current(), []);

  const refreshUsage = useCallback(async () => {
    const result = await call<UsageSummary>(api, '/api/usage');
    setUsage(result.ok ? result.data : null);
  }, [api]);

  useEffect(() => {
    if (stage === 'loading' || stage === 'denied') return;
    void refreshUsage();
    const refresh = () => { void refreshUsage(); };
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, 60_000);
    return () => { window.removeEventListener('focus', refresh); window.clearInterval(timer); };
  }, [stage, refreshUsage]);

  useEffect(() => {
    if (injected) return;
    let live = true;
    createAnalytics(userId ?? undefined, api)
      .then((a) => live && setAnalytics(a))
      .catch(() => live && setAnalytics(nullAnalytics()));
    return () => { live = false; stopAnalytics(); };
  }, [injected, userId, api]);

  // Ask the Worker who we are and whether we are allowed, then load the profile.
  useEffect(() => {
    let live = true;

    (async () => {
      const me = await call<{ userId: string; email: string | null }>(api, '/api/me');
      if (!live) return;

      if (me.ok) setEmail(me.data.email ?? null);

      if (!me.ok) {
        // 403 and 401 mean opposite things and must not render the same screen. 403 is
        // "we know who you are and you are not on the list" — a question for whoever owns
        // access. 401 is "we could not verify your session at all", which is almost always
        // configuration rather than permission, and sending someone to argue about the
        // allowlist when their Clerk keys are mismatched wastes an afternoon.
        setDeniedReason(
          me.status === 403 ? me.reason : me.status === 401 ? 'session_invalid' : 'unreachable',
        );
        setStage('denied');
        return;
      }

      const existing = await call<{ profile: StandardsDefault; complete: boolean }>(api, '/api/profile');
      if (!live) return;

      // Straight to the uploader only on a complete profile. A partial one — another Labs
      // tool filled in some of it — opens the form with those fields already chosen.
      const complete = existing.ok && existing.data.complete ? completeProfile(existing.data.profile) : null;
      if (complete) {
        setProfile(complete);
        setStage('upload');
      } else {
        setDraft(existing.ok ? existing.data.profile : null);
        setStage('profile');
      }
    })();

    return () => { live = false; };
  }, [api]);

  const variant = useCallback(() => analytics?.variant() ?? 'default', [analytics]);

  /**
   * What this account may spend, as the Worker reported it. Until `/api/usage` answers, the
   * teacher pair stands in — understating an allowance is recoverable, promising fifty
   * attempts to someone who has five is not.
   */
  const limits: QuotaLimits = usage
    ? { uploads: usage.uploads.limit, analyses: usage.analyses.limit }
    : TEACHER_LIMITS;

  // Once, when analytics first becomes available — not once per stage, and not twice under
  // StrictMode's double-invocation.
  const viewed = useRef(false);
  useEffect(() => {
    if (!analytics || viewed.current) return;
    viewed.current = true;
    track(objectivesPageViewed(analytics.variant()));
  }, [analytics, track]);

  const profileFacts = (value: Profile | null = profile) => ({
    state: value?.jurisdiction ?? '',
    subject: value?.academicSubject ?? '',
    grades: [...(value?.gradeLevels ?? [])],
  });

  async function saveProfile(next: Profile) {
    track(previewObjectivesAction('profile_save_clicked', profileFacts(next)));
    setBusy(true);
    setProfileError(null);

    const result = await call<{ profile: Profile }>(api, '/api/profile', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(next),
    });
    setBusy(false);

    if (!result.ok) {
      setProfileError({
        ...(result.field ? { field: result.field } : {}),
        message: result.status === 0
          ? 'Something went wrong on our end. Try again in a moment.'
          : 'That combination was refused. Check the state, subject and grade.',
      });
      return;
    }

    setProfile(result.data.profile);
    track(objectivesProfileSaved(next.jurisdiction, next.academicSubject, next.gradeLevels.length, variant()));
    setStage('upload');
  }

  async function uploadAndRead(file: File) {
    setBusy(true);
    setUploadError(null);
    const result = await runUploadAndRead(file, {
      call: (path, init) => call(api, path, init),
      track,
      refreshUsage: () => void refreshUsage(),
      now: () => performance.now(),
      variant: variant(),
      facts: profileFacts(),
      limits,
    });
    setBusy(false);
    if (!result.ok) {
      setUploadError(result.message);
      return;
    }
    setReport(result.report);
    track(objectivesReportViewed(result.report.analysis.findings.length, variant()));
    setStage('report');
  }

  /**
   * The teacher's own delete. The Worker removes the rows and both objects; the page just
   * goes back to the uploader. A failure keeps the report on screen with the reason, so a
   * teacher who wanted something gone knows it is not.
   */
  async function deletePlan() {
    if (!report) return;
    track(previewObjectivesAction('delete_confirmed'));
    setDeleting(true);
    const result = await call<{ deleted: string }>(api, `/api/documents/${report.document.id}`, {
      method: 'DELETE',
    });
    setDeleting(false);

    if (!result.ok) {
      track(previewObjectivesAction('delete_failed', { reason: result.error }));
      setDeleteError('We could not finish deleting that plan. Try again in a moment.');
      return;
    }

    setReport(null);
    setDeleteError(null);
    setUploadError(null);
    setStage('upload');
  }

  const resolving = analytics === null;
  // The same either/or rule as access, on the same function: a 240 address uploads no
  // matter what the gate says, and the gate opens uploads for everyone else. The email is
  // the Worker-verified one from /api/me, not a claim the page derived itself.
  const uploadGated =
    !resolving && !decideAccess({ email, disabled: !analytics.gate(UPLOAD_GATE) }).allowed;

  return (
    <>
      {/* The bar the study app and 240 Labs both put at the top of every page, copied from
          `packages/one-step-ahead`: the orange mark and this tool's name at the left, the Labs
          chip and the account control at the right, full window width. */}
      <ObjectivesHeader headerAction={headerAction} onToolsListOpened={() => track(objectivesToolsListOpened())} />

    <main className="page">
      {stage === 'profile' || stage === 'upload' ? (
        <ObjectivesWelcome surface="signed_in" onTrack={track} onUseOwnPlan={() => focusOwnPlan(ownPlanRef.current)} />
      ) : (
        <div className="intro">
          <h1>Standard Checker</h1>
          <p className="tagline">Which standards does this material match?</p>
        </div>
      )}

      <div ref={ownPlanRef} tabIndex={-1} className="own-plan">

      {stage === 'loading' && <p className="hint" role="status">Checking your access…</p>}

      {stage === 'denied' && <AccessDenied {...(deniedReason ? { reason: deniedReason } : {})} />}

      {stage !== 'loading' && stage !== 'denied' && (
        <div className="hint" role="status" aria-live="polite">
          {usage ? (
            <>
              <strong>{usage.analyses.remaining} of {usage.analyses.limit} analysis attempts remaining</strong>
              {' · '}{usage.uploads.remaining} of {usage.uploads.limit} uploads remaining in the past seven days.
              {usage.analyses.remaining === 0 && usage.analyses.nextAvailableAt && (
                <> Next analysis available {new Date(usage.analyses.nextAvailableAt).toLocaleString()}.</>
              )}
              {usage.uploads.remaining === 0 && usage.uploads.nextAvailableAt && (
                <> Next upload available {new Date(usage.uploads.nextAvailableAt).toLocaleString()}.</>
              )}
            </>
          ) : 'Attempt count is currently unavailable.'}
        </div>
      )}

      {stage === 'profile' && (
        <ProfileForm initial={profile ?? draft} saving={busy} error={profileError} onSave={saveProfile} onTrack={track} />
      )}

      {stage === 'upload' && (
        <>
          <Uploader
            disabled={resolving}
            gated={uploadGated}
            limits={limits}
            busy={busy}
            error={uploadError}
            onUpload={uploadAndRead}
            onTrack={track}
            onRejected={(reason) => track(objectivesUploadRejected(reason))}
          />
          {profile && (
            <p className="hint">
              Matching standards for {profile.jurisdiction}, {profile.academicSubject}, grade{' '}
              {profile.gradeLevels.join(', ')}.{' '}
              <button className="link" type="button" onClick={() => { track(previewObjectivesAction('profile_change_clicked', { from: 'upload' })); setStage('profile'); }}>
                Change
              </button>
            </p>
          )}
        </>
      )}

      {stage === 'report' && report?.analysis && (
        <>
          {deleteError && <p className="error" role="alert">{deleteError}</p>}
          <Report
            analysis={report.analysis}
            extractedText={report.extractedText}
            variant={analytics?.reportVariant() ?? 'ranked'}
            onStartOver={() => { track(previewObjectivesAction('read_another_clicked')); setReport(null); setUploadError(null); setDeleteError(null); setStage('upload'); }}
            onChangeProfile={() => { track(previewObjectivesAction('profile_change_clicked', { from: 'report' })); setProfileError(null); setStage('profile'); }}
            onViewToggled={(view) => track(objectivesViewToggled(view))}
            onDelete={deletePlan}
            onTrack={track}
            deleting={deleting}
          />
        </>
      )}
      </div>
    </main>
    </>
  );
}

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/**
 * The browser-test identity.
 *
 * When set, it wins — including over a compiled-in publishable key. That is deliberate and
 * it is not the security boundary. Which component tree renders cannot grant anyone access
 * to anything; the Worker decides that, and its rule is unchanged: the bypass is honoured
 * only when no `CLERK_SECRET_KEY` exists. A production build has a secret, so a production
 * build refuses these tokens no matter what the browser rendered.
 *
 * The earlier condition also required the publishable key to be *absent*, which coupled the
 * test path to whether a developer happened to have `.env.local` on disk. The suite then
 * tested something different on a configured machine than in CI — the exact failure this
 * file's sibling `BLANKED_KEYS` exists to prevent, one layer up. `VITE_E2E_IDENTITY` is
 * empty in every build that does not deliberately set it, which is the guard that matters
 * here.
 */
const e2eIdentity = import.meta.env.VITE_E2E_IDENTITY;

function ClerkAuthed() {
  const { getToken, userId } = useAuth();
  return (
    <ObjectivesApp auth={{ getToken, userId: userId ?? null }} headerAction={<UserButton />} />
  );
}

export default function App() {
  if (e2eIdentity) {
    return (
      <>
        {/*
          * Loud on purpose.
          *
          * This branch wins over a compiled-in publishable key, which is what the browser
          * suite needs — but it means a stray VITE_E2E_IDENTITY at build time produces a
          * bundle that silently stops using Clerk and sends a bare string where a JWT
          * belongs. The Worker refuses it correctly, and the resulting
          * "JWSInvalid: Invalid Compact JWS" in the logs points nowhere near the cause.
          *
          * A banner costs nothing in the only two places this can appear — the browser
          * tests, and a mistake — and turns ten minutes of confusion into a glance.
          */}
        <div className="test-identity-banner" role="status">
          Test identity build — sign-in is bypassed as{' '}
          <code>{e2eIdentity.split(':')[0]}</code>. Not for production. Rebuild without{' '}
          <code>VITE_E2E_IDENTITY</code> to restore Clerk.
        </div>
        <ObjectivesApp
          auth={{ getToken: async () => e2eIdentity, userId: e2eIdentity.split(':')[0] || null }}
        />
      </>
    );
  }

  // The sample works without credentials; processing a teacher's plan still needs sign-in.
  if (!publishableKey) {
    return (
      <PublicObjectives signIn={
        <div className="notice" role="status">
          <h2>Sign-in is not configured.</h2>
          <p>
            VITE_CLERK_PUBLISHABLE_KEY was not set when this bundle was built. See
            packages/objectives/README.md.
          </p>
        </div>
      } />
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      /*
       * Force, not fallback, and all four of them.
       *
       * With none of these set, Clerk uses whatever the *instance* is configured to do —
       * and 240's production instance sends people to study.240tutoring.com/courses,
       * because that is what the study platform needs. A teacher signing in to 240 Labs
       * would land on the study platform, which is a launch bug that would not show up in
       * local development at all: the development instance has no such default.
       *
       * `fallback` variants only apply when no redirect is already in play, so they lose
       * to the instance default. `force` is the one that actually keeps a 240 Labs user in
       * 240 Labs.
       */
      signInForceRedirectUrl="/"
      signUpForceRedirectUrl="/"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/"
    >
      <SignedOut>
        <PublicObjectives signIn={<SignIn routing="hash" />} />
      </SignedOut>
      <SignedIn>
        <ClerkAuthed />
      </SignedIn>
    </ClerkProvider>
  );
}
